import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
  Card,
  CardHeader,
  IconButton,
  Tooltip,
  CardContent,
  Button,
  Typography,
  FormHelperText,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Menu,
  MenuItem
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { connect } from "react-redux";
import { compose } from "../../utils/compose";
import { paymentMethodsActions } from "../../store/actions/paymentMethods/paymentMethods";
import InlineSpinner from "../UI/InlineSpinner/InlineSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { useTranslation } from "react-i18next";
import DeleteIcon from '@material-ui/icons/Delete';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

const useStyles = makeStyles(theme => ({
  card: {},
  cardBrand: {
    fontSize: "2rem"
  },
  cardTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  withDivider: {
    '& li:not(:last-child)': {
      borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
    }
  }
}));

const PaymentMethodSummary = props => {
  const classes = useStyles();
  const {
    onAdd,
    billingInformationExists,
    onFetchPaymentMethods,
    paymentMethodList,
    onRemovePaymentMethod,
    onSetAsPrimaryPaymentMethod
  } = props;
  const [pmMenuAnchorEl, setPmMenuAnchorEl] = useState(null);
  const [pmMenuSelectedIndex, setPmMenuSelectedIndex] = useState(null);
  const [ removeConfirmOpen, setRemoveConfirmOpen ] = useState(false);
  const pmMenuOpen = Boolean(pmMenuAnchorEl);
  const { t } = useTranslation();

  useEffect(() => {
    onFetchPaymentMethods();
  }, []);

  const addHandler = () => {
    onAdd();
  };

  const firstPaymentMethod = () => (
    <CardContent>
      <div style={{ textAlign: "center" }}>
        <Button
          color="primary"
          variant="contained"
          onClick={addHandler}
          disabled={!billingInformationExists}
        >
          {t('addPaymentMethod')}
        </Button>
        {!billingInformationExists ? (
          <FormHelperText className="Mui-error" style={{ textAlign: "center" }}>
            {t('paymentMethodBillingInfoWarning')}
          </FormHelperText>
        ) : null}
      </div>
    </CardContent>
  );

  const loading = () => (
    <CardContent>
      <InlineSpinner size="small" align="center" />
    </CardContent>
  );

  const pmMenuOpenHandler = (event, index) => {
    setPmMenuAnchorEl(event.currentTarget);
    setPmMenuSelectedIndex(index);
  }

  const pmMenuCloseHandler = () => {
    setPmMenuAnchorEl(null);
    setPmMenuSelectedIndex(null);
  }

  const setAsPrimaryHandler = () => {
    console.log(`Set as primary ${pmMenuSelectedIndex}`);
    onSetAsPrimaryPaymentMethod(paymentMethodList.data[pmMenuSelectedIndex].Id);
    pmMenuCloseHandler();
  }

  const deleteHandler = () => {
    console.log(`Delete ${pmMenuSelectedIndex}`);
    // onRemovePaymentMethod(paymentMethodList.data[pmMenuSelectedIndex].Id);
    // pmMenuCloseHandler();
    setRemoveConfirmOpen(true);

  }

  const removeConfirmCloseHandler = (value) => {
    if(value === true) {
      onRemovePaymentMethod(paymentMethodList.data[pmMenuSelectedIndex].Id);
      pmMenuCloseHandler();
      setRemoveConfirmOpen(false);
    } else {
      pmMenuCloseHandler();
      setRemoveConfirmOpen(false);
    }
  }

  const getPaymentMethodActionMenu = () => {
    return (
      <Menu
        id="pm-menu"
        anchorEl={pmMenuAnchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        keepMounted
        open={pmMenuOpen}
        onClose={pmMenuCloseHandler}
        PaperProps={{
          style: {
            maxHeight: 48 * 4.5
          }
        }}
      >
        <MenuItem onClick={setAsPrimaryHandler}>
          <ListItemIcon>
            <FontAwesomeIcon icon={["far", "credit-card"]} />
          </ListItemIcon>
          <ListItemText primary={t('setAsPrimaryPaymentMethod')} />
        </MenuItem>
        <MenuItem onClick={deleteHandler}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText primary={t("delete")} />
        </MenuItem>
      </Menu>
    )
  }

  const getCardIcon = (brand) => {
    switch(brand) {
      case "Visa":
        return <FontAwesomeIcon icon={["fab", "cc-visa"]} />
      case "Mastercard":
        return <FontAwesomeIcon icon={["fab", "cc-mastercard"]} />
      case "AmericanExpress":
        return <FontAwesomeIcon icon={["fab", "cc-amex"]} />
      case "Discover":
        return <FontAwesomeIcon icon={["fab", "cc-discover"]} />
      case "Jcb":
        return <FontAwesomeIcon icon={["fab", "cc-jcb"]} />
      case "DinersClub":
        return <FontAwesomeIcon icon={["fab", "cc-diners-club"]} />
      default:
        return <FontAwesomeIcon icon={["far", "credit-card"]} />
    }
  }



  const getPaymentMethodListItem = (p, index) => {
    switch(p.Type) {
      case 1:
        return (
          <ListItem>
            <ListItemIcon className={classes.cardBrand}>
              {getCardIcon(p.Card.Brand)}
            </ListItemIcon>
            <ListItemText
              primary={p.Card.MaskedNumber}
              secondary={`${p.Card.FirstName} ${p.Card.LastName}, ${t("cardExpiresOn")}: ${p.Card.ExpiryMonth} / ${p.Card.ExpiryYear}`}
            />
            <ListItemSecondaryAction>
              <IconButton onClick={ev => pmMenuOpenHandler(ev, index)} disabled={p.Processing}>
                <MoreVertIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        );
      default:
          return (
            <ListItem>
              <ListItemIcon className={classes.cardBrand}>
                {getCardIcon("other")}
              </ListItemIcon>
              <ListItemText
                primary={p.Card.MaskedNumber}
                secondary={`${p.Card.ExpiryMonth} / ${p.Card.ExpiryYear}`}
              />
              <ListItemSecondaryAction>
                <IconButton onClick={ev => pmMenuOpenHandler(ev, index)} disabled={p.Processing}>
                  <MoreVertIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          );

    }
  }

  const paymentMethodSummary = () => (
    <CardContent>
      <List dense={true} className={classes.withDivider}>
        {paymentMethodList.data.map((p, index) => (
          <React.Fragment key={p.Id}>
          {getPaymentMethodListItem(p, index)}
          {/* <Divider component="li" /> */}
          </React.Fragment >
        ))}

      </List>
      {getPaymentMethodActionMenu()}
      <ConfirmDialog
        onClose={removeConfirmCloseHandler}
        open={removeConfirmOpen}
        title={t('pleaseVerify')}
        content={
          <>
            <Typography variant="body1" paragraph>
              {t('deletePaymentMethodConfirmation')}
            </Typography>
          </>
        }
        paragraph={false}
      />
    </CardContent>
  );
//warnSinglePaymentMethod
  return (
    <Card className={classes.card}>
      <CardHeader
        className={classes.cardTitle}
        title={t('paymentMethodSummaryTitle')}
        action={
          <Tooltip
            title={
              !billingInformationExists
                ? t('paymentMethodBillingInfoWarning')
                : (paymentMethodList.data.length > 0) ? t('warnSinglePaymentMethod') : t('addPaymentMethod')
            }
          >
            <div>
              <IconButton
                className={classes.action}
                onClick={addHandler}
                disabled={!billingInformationExists || paymentMethodList.data.length > 0}
                color="inherit"
              >
                <AddIcon />
              </IconButton>
            </div>
          </Tooltip>
        }
      />
      {paymentMethodList.fetching
        ? loading()
        : paymentMethodList.data.length === 0
          ? firstPaymentMethod()
          : paymentMethodSummary()

        }
    </Card>
  );
};

PaymentMethodSummary.propTypes = {
  onAdd: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  billingInformationExists: state.billingInformation.data.BillingInformation,
  paymentMethodList: state.paymentMethods.list
});

const mapDispatchToProps = dispatch => ({
  onFetchPaymentMethods: () => dispatch(paymentMethodsActions.fetch()),
  onRemovePaymentMethod: (id) => dispatch(paymentMethodsActions.remove(id)),
  onSetAsPrimaryPaymentMethod: (id) => dispatch(paymentMethodsActions.setAsPrimary(id)),
});

const composed = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(PaymentMethodSummary);

export default composed;
